// QR Code generator — byte mode, error correction level M, versions 1–10
// Based on ISO/IEC 18004. No external dependencies.

// ── GF(256) ──────────────────────────────────────────────────────────────────

const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);

(function initGF() {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = x;
    GF_LOG[x] = i;
    x <<= 1;
    if (x & 256) x ^= 0x11d; // primitive polynomial x^8+x^4+x^3+x^2+1
  }
  for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255];
})();

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return GF_EXP[(GF_LOG[a] + GF_LOG[b]) % 255];
}

function makeGenPoly(degree: number): number[] {
  let g = [1];
  for (let i = 0; i < degree; i++) {
    const next = new Array(g.length + 1).fill(0);
    for (let j = 0; j < g.length; j++) {
      next[j] ^= g[j];
      next[j + 1] ^= gfMul(g[j], GF_EXP[i]);
    }
    g = next;
  }
  return g;
}

function computeEC(data: number[], ecLen: number): number[] {
  const gen = makeGenPoly(ecLen);
  const rem = [...data, ...new Array(ecLen).fill(0)];
  for (let i = 0; i < data.length; i++) {
    const c = rem[i];
    if (c !== 0) {
      for (let j = 0; j < gen.length; j++) rem[i + j] ^= gfMul(gen[j], c);
    }
  }
  return rem.slice(data.length);
}

// ── Version table (ECL M only) ────────────────────────────────────────────────
// [dataBytes, ecPerBlock, blocksG1, dataPerG1, blocksG2, dataPerG2]
const V: [number, number, number, number, number, number][] = [
  [0, 0, 0, 0, 0, 0], // 0 unused
  [16, 10, 1, 16, 0, 0], // 1
  [28, 16, 1, 28, 0, 0], // 2
  [44, 26, 1, 44, 0, 0], // 3
  [64, 18, 2, 32, 0, 0], // 4
  [86, 24, 2, 43, 0, 0], // 5
  [108, 16, 4, 27, 0, 0], // 6
  [124, 18, 4, 31, 0, 0], // 7
  [154, 22, 2, 38, 2, 39], // 8
  [182, 22, 3, 36, 2, 37], // 9
  [216, 26, 4, 43, 1, 44], // 10
  [254, 22, 1, 50, 4, 51], // 11
  [290, 22, 6, 36, 2, 37], // 12
  [334, 22, 8, 37, 1, 38], // 13
  [365, 24, 4, 40, 5, 41], // 14
  [415, 24, 5, 41, 5, 42], // 15
  [453, 28, 7, 45, 3, 46], // 16
  [507, 28, 10, 46, 1, 47], // 17
  [563, 26, 9, 43, 4, 44], // 18
  [627, 26, 3, 44, 11, 45], // 19
  [669, 26, 3, 41, 13, 42], // 20
  [714, 26, 17, 42, 0, 0], // 21
  [782, 28, 17, 46, 0, 0], // 22
  [860, 28, 4, 47, 14, 48], // 23
  [914, 28, 6, 45, 14, 46], // 24
  [1000, 28, 8, 47, 13, 48], // 25
  [1062, 28, 19, 46, 4, 47], // 26
  [1128, 28, 22, 45, 3, 46], // 27
  [1193, 28, 3, 45, 23, 46], // 28
  [1267, 28, 21, 45, 7, 46], // 29
  [1373, 28, 19, 47, 10, 48], // 30
  [1455, 28, 2, 46, 29, 47], // 31
  [1541, 28, 10, 46, 23, 47], // 32
  [1631, 28, 14, 46, 21, 47], // 33
  [1725, 28, 14, 46, 23, 47], // 34
  [1812, 28, 12, 47, 26, 48], // 35
  [1914, 28, 6, 47, 34, 48], // 36
  [1992, 28, 29, 46, 14, 47], // 37
  [2102, 28, 13, 46, 32, 47], // 38
  [2216, 28, 40, 47, 7, 48], // 39
  [2334, 28, 18, 47, 31, 48], // 40
];

// Alignment pattern center coordinates per version (versions 2–25)
const ALIGN: number[][] = [
  [],
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 28, 46],
  [6, 32, 50],
  [6, 30, 54],
  [6, 32, 58],
  [6, 34, 62],
  [6, 26, 46, 66],
  [6, 26, 48, 70],
  [6, 26, 50, 74],
  [6, 30, 54, 78],
  [6, 30, 56, 82],
  [6, 30, 58, 86],
  [6, 34, 62, 90],
  [6, 28, 50, 72, 94],
  [6, 26, 50, 74, 98],
  [6, 30, 54, 78, 102],
  [6, 28, 54, 80, 106],
  [6, 32, 58, 84, 110],
  [6, 30, 58, 86, 114],
  [6, 34, 62, 90, 118],
  [6, 26, 50, 74, 98, 122],
  [6, 30, 54, 78, 102, 126],
  [6, 26, 52, 78, 104, 130],
  [6, 30, 56, 82, 108, 134],
  [6, 34, 60, 86, 112, 138],
  [6, 30, 58, 86, 114, 142],
  [6, 34, 62, 90, 118, 146],
  [6, 30, 54, 78, 102, 126, 150],
  [6, 24, 50, 76, 102, 128, 154],
  [6, 28, 54, 80, 106, 132, 158],
  [6, 32, 58, 84, 110, 136, 162],
  [6, 26, 54, 82, 110, 138, 166],
  [6, 30, 58, 86, 114, 142, 170],
];

// Format info for ECL M (bits: 01) XOR'd with mask 101010000010010
const FMT: number[] = [
  0x5412,
  0x5125,
  0x5e7c,
  0x5b4b,
  0x45f9,
  0x40ce,
  0x4f97,
  0x4aa0,
];

// Version info words for versions 7–25 (BCH encoded per ISO/IEC 18004)
const VER_INFO: number[] = [
  0,
  0,
  0,
  0,
  0,
  0,
  0,
  0x07c94,
  0x085bc,
  0x09a99,
  0x0a4d3,
  0x0bbf6,
  0x0c762,
  0x0d847,
  0x0e60d,
  0x0f928,
  0x10b78,
  0x1145d,
  0x12a17,
  0x13532,
  0x149a6,
  0x15683,
  0x168c9,
  0x177ec,
  0x18ec4,
  0x191e1,
  0x1afab,
  0x1b08e,
  0x1cc1a,
  0x1d33f,
  0x1ed75,
  0x1f250,
  0x209d5,
  0x216f0,
  0x228ba,
  0x2379f,
  0x24b0b,
  0x2542e,
  0x26a64,
  0x27541,
  0x28c69,
];

type Matrix = number[][]; // 0 = light, 1 = dark, -1 = reserved (unset)

function newMatrix(n: number): Matrix {
  return Array.from({ length: n }, () => new Array(n).fill(-1));
}

// ── Finder pattern ────────────────────────────────────────────────────────────

function placeFinder(m: Matrix, r: number, c: number): void {
  const n = m.length;
  for (let dr = -1; dr <= 7; dr++) {
    for (let dc = -1; dc <= 7; dc++) {
      const rr = r + dr;
      const cc = c + dc;
      if (rr < 0 || rr >= n || cc < 0 || cc >= n) continue;
      if (dr >= 0 && dr <= 6 && dc >= 0 && dc <= 6) {
        const dark =
          dr === 0 ||
          dr === 6 ||
          dc === 0 ||
          dc === 6 ||
          (dr >= 2 && dr <= 4 && dc >= 2 && dc <= 4);
        m[rr][cc] = dark ? 1 : 0;
      } else {
        m[rr][cc] = 0; // separator
      }
    }
  }
}

// ── Alignment pattern ─────────────────────────────────────────────────────────

function placeAlignment(m: Matrix, r: number, c: number): void {
  for (let dr = -2; dr <= 2; dr++) {
    for (let dc = -2; dc <= 2; dc++) {
      const dark =
        Math.abs(dr) === 2 || Math.abs(dc) === 2 || (dr === 0 && dc === 0);
      m[r + dr][c + dc] = dark ? 1 : 0;
    }
  }
}

// ── Timing patterns ───────────────────────────────────────────────────────────

function placeTiming(m: Matrix): void {
  const n = m.length;
  for (let i = 8; i < n - 8; i++) {
    if (m[6][i] === -1) m[6][i] = i % 2 === 0 ? 1 : 0;
    if (m[i][6] === -1) m[i][6] = i % 2 === 0 ? 1 : 0;
  }
}

// ── Format information ────────────────────────────────────────────────────────

function placeFormat(m: Matrix, maskIdx: number): void {
  const n = m.length;
  const bits = FMT[maskIdx];

  // Positions around top-left finder
  const pos = [
    [8, 0],
    [8, 1],
    [8, 2],
    [8, 3],
    [8, 4],
    [8, 5],
    [8, 7],
    [8, 8],
    [7, 8],
    [5, 8],
    [4, 8],
    [3, 8],
    [2, 8],
    [1, 8],
    [0, 8],
  ];
  for (let i = 0; i < 15; i++) {
    const bit = (bits >> (14 - i)) & 1;
    m[pos[i][0]][pos[i][1]] = bit;
    // Mirror: bottom-left and top-right
    if (i < 7) {
      m[n - 1 - i][8] = bit;
    } else {
      m[8][n - 15 + i] = bit;
    }
  }
  m[n - 8][8] = 1; // dark module
}

// ── Version information (V7+) ─────────────────────────────────────────────────

function placeVersionInfo(m: Matrix, version: number): void {
  const n = m.length;
  const info = VER_INFO[version];
  for (let i = 0; i < 18; i++) {
    const bit = (info >> i) & 1;
    m[Math.floor(i / 3)][n - 11 + (i % 3)] = bit; // top-right block
    m[n - 11 + (i % 3)][Math.floor(i / 3)] = bit; // bottom-left block
  }
}

// ── Mask functions ────────────────────────────────────────────────────────────

const MASKS: Array<(r: number, c: number) => boolean> = [
  (r, c) => (r + c) % 2 === 0,
  (r, _c) => r % 2 === 0,
  (_r, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
];

function applyMask(m: Matrix, reserved: boolean[][], maskIdx: number): void {
  const fn = MASKS[maskIdx];
  for (let r = 0; r < m.length; r++) {
    for (let c = 0; c < m.length; c++) {
      if (!reserved[r][c] && fn(r, c)) {
        m[r][c] ^= 1;
      }
    }
  }
}

// Penalty score for mask evaluation
function penaltyScore(m: Matrix): number {
  const n = m.length;
  let score = 0;

  // Rule 1: 5+ in a row
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; ) {
      const val = m[r][c];
      let len = 1;
      while (c + len < n && m[r][c + len] === val) len++;
      if (len >= 5) score += 3 + (len - 5);
      c += len;
    }
  }
  for (let c = 0; c < n; c++) {
    for (let r = 0; r < n; ) {
      const val = m[r][c];
      let len = 1;
      while (r + len < n && m[r + len][c] === val) len++;
      if (len >= 5) score += 3 + (len - 5);
      r += len;
    }
  }

  // Rule 2: 2x2 blocks
  for (let r = 0; r < n - 1; r++) {
    for (let c = 0; c < n - 1; c++) {
      const v = m[r][c];
      if (v === m[r][c + 1] && v === m[r + 1][c] && v === m[r + 1][c + 1]) {
        score += 3;
      }
    }
  }

  // Rule 3: finder-like patterns
  const p1 = [1, 0, 1, 1, 1, 0, 1, 0, 0, 0, 0];
  const p2 = [0, 0, 0, 0, 1, 0, 1, 1, 1, 0, 1];
  for (let r = 0; r < n; r++) {
    for (let c = 0; c <= n - 11; c++) {
      const rowOk1 = p1.every((v, i) => m[r][c + i] === v);
      const rowOk2 = p2.every((v, i) => m[r][c + i] === v);
      if (rowOk1 || rowOk2) score += 40;
      const colOk1 = p1.every((v, i) => m[c + i][r] === v);
      const colOk2 = p2.every((v, i) => m[c + i][r] === v);
      if (colOk1 || colOk2) score += 40;
    }
  }

  // Rule 4: dark module ratio
  let dark = 0;
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) dark += m[r][c];
  const pct = (dark / (n * n)) * 100;
  const prev5 = Math.floor(pct / 5) * 5;
  const next5 = prev5 + 5;
  score += 10 * Math.min(Math.abs(prev5 - 50) / 5, Math.abs(next5 - 50) / 5);

  return score;
}

// ── Data encoding (byte mode) ─────────────────────────────────────────────────

function encodeData(text: string, version: number): number[] {
  const bytes = Array.from(text).map((ch) => ch.charCodeAt(0));
  const bits: number[] = [];

  // Mode indicator: 0100 (byte)
  bits.push(0, 1, 0, 0);
  // Character count: 8 bits for V1–9, 16 bits for V10+
  const len = bytes.length;
  const countBits = version >= 10 ? 16 : 8;
  for (let i = countBits - 1; i >= 0; i--) bits.push((len >> i) & 1);
  // Data bytes
  for (const b of bytes) {
    for (let i = 7; i >= 0; i--) bits.push((b >> i) & 1);
  }
  // Terminator (up to 4 zero bits)
  const totalBits = V[version][0] * 8;
  for (let i = 0; i < 4 && bits.length < totalBits; i++) bits.push(0);
  // Pad to byte boundary
  while (bits.length % 8 !== 0) bits.push(0);
  // Padding codewords
  const pad = [0xec, 0x11];
  let pi = 0;
  while (bits.length < totalBits) {
    const b = pad[pi % 2];
    for (let i = 7; i >= 0; i--) bits.push((b >> i) & 1);
    pi++;
  }
  // Convert to bytes
  const cws: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let val = 0;
    for (let j = 0; j < 8; j++) val = (val << 1) | bits[i + j];
    cws.push(val);
  }
  return cws;
}

// ── Block interleaving ────────────────────────────────────────────────────────

function buildCodewords(text: string, version: number): number[] {
  const [, ecPerBlock, bg1, dg1, bg2, dg2] = V[version];
  const dataCWs = encodeData(text, version);

  // Split into blocks
  const blocks: number[][] = [];
  let offset = 0;
  for (let i = 0; i < bg1; i++) {
    blocks.push(dataCWs.slice(offset, offset + dg1));
    offset += dg1;
  }
  for (let i = 0; i < bg2; i++) {
    blocks.push(dataCWs.slice(offset, offset + dg2));
    offset += dg2;
  }

  // EC for each block
  const ecBlocks = blocks.map((b) => computeEC(b, ecPerBlock));

  // Interleave data
  const result: number[] = [];
  const maxData = Math.max(dg1, dg2);
  for (let i = 0; i < maxData; i++) {
    for (const b of blocks) {
      if (i < b.length) result.push(b[i]);
    }
  }
  // Interleave EC
  for (let i = 0; i < ecPerBlock; i++) {
    for (const e of ecBlocks) result.push(e[i]);
  }
  return result;
}

// ── Place data modules ────────────────────────────────────────────────────────

function placeData(
  m: Matrix,
  reserved: boolean[][],
  codewords: number[]
): void {
  const n = m.length;
  let bitIdx = 0;
  const totalBits = codewords.length * 8;

  let c = n - 1;
  let goingUp = true;

  while (c > 0) {
    if (c === 6) c--; // skip timing column

    for (let rowIdx = 0; rowIdx < n; rowIdx++) {
      const r = goingUp ? n - 1 - rowIdx : rowIdx;
      for (let dc = 0; dc < 2; dc++) {
        const col = c - dc;
        if (!reserved[r][col]) {
          if (bitIdx < totalBits) {
            const bit =
              (codewords[Math.floor(bitIdx / 8)] >> (7 - (bitIdx % 8))) & 1;
            m[r][col] = bit;
          } else {
            m[r][col] = 0;
          }
          bitIdx++;
        }
      }
    }

    c -= 2;
    goingUp = !goingUp;
  }
}

// ── Main export ───────────────────────────────────────────────────────────────

export function generateQRMatrix(text: string): boolean[][] {
  const bytes = text.length; // byte mode: 1 char = 1 byte for ASCII/Latin-1

  // Choose minimum version that fits (overhead: 12 bits for V1–9, 20 bits for V10+)
  let version = 1;
  while (version <= 40) {
    const overhead = version >= 10 ? 3 : 2;
    if (V[version][0] - overhead >= bytes) break;
    version++;
  }
  if (version > 40) throw new Error("Input too long for QR version 1–40");

  const n = 4 * version + 17;
  const m = newMatrix(n);

  // Place finder patterns + separators
  placeFinder(m, 0, 0);
  placeFinder(m, n - 7, 0);
  placeFinder(m, 0, n - 7);

  // Timing patterns
  placeTiming(m);

  // Alignment patterns
  const ap = ALIGN[version];
  for (const r of ap) {
    for (const c of ap) {
      if (m[r][c] === -1) placeAlignment(m, r, c);
    }
  }

  // Mark reserved modules (format info, dark module)
  placeFormat(m, 0); // temporary — overwritten after masking

  // Version information modules for V7+ (not affected by masking)
  if (version >= 7) placeVersionInfo(m, version);

  // Build reserved map (true = function pattern, don't mask/overwrite with data)
  const reserved: boolean[][] = Array.from({ length: n }, (_, r) =>
    Array.from({ length: n }, (__, c) => m[r][c] !== -1)
  );

  // Place data
  const codewords = buildCodewords(text, version);
  placeData(m, reserved, codewords);

  // Evaluate all 8 masks, pick lowest penalty
  let bestMask = 0;
  let bestScore = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    const copy: Matrix = m.map((row) => [...row]);
    applyMask(copy, reserved, mask);
    placeFormat(copy, mask);
    const s = penaltyScore(copy);
    if (s < bestScore) {
      bestScore = s;
      bestMask = mask;
    }
  }

  // Apply winning mask
  applyMask(m, reserved, bestMask);
  placeFormat(m, bestMask);

  // Convert to boolean matrix (true = dark)
  return m.map((row) => row.map((v) => v === 1));
}
